
import { useEffect, useState } from 'react';

const LedWallSystem = () => {
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-20 overflow-hidden bg-gray-900">
      {/* LED Panel Background */}
      <div className="absolute inset-0">
        <div className="grid grid-cols-12 h-full opacity-20">
          {Array.from({ length: 144 }).map((_, i) => (
            <div
              key={i}
              className={`border border-blue-500/20 ${
                animationPhase === Math.floor(i / 36) ? 'bg-blue-500/10' : 'bg-gray-800/50'
              } transition-all duration-1000`}
              style={{
                animationDelay: `${i * 0.05}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Animated Light Beams */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-60 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-transparent via-blue-400 to-transparent opacity-40 animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute right-0 top-0 w-1 h-full bg-gradient-to-b from-transparent via-cyan-400 to-transparent opacity-40 animate-pulse" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Main Title with LED Glow Effect */}
        <div className="relative mb-8">
          <h2 
            className="text-5xl md:text-7xl font-bold mb-4 relative inline-block"
            style={{
              background: 'linear-gradient(45deg, #00f5ff, #0080ff, #00f5ff, #ffffff)',
              backgroundSize: '300% 300%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'ledGlow 3s ease-in-out infinite, textShine 4s linear infinite'
            }}
          >
            ALP BUILD
          </h2>
          
          {/* Glowing underline */}
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-80 animate-pulse" />
          
          <h3 
            className="text-3xl md:text-5xl font-bold text-white relative"
            style={{
              textShadow: '0 0 20px #00f5ff, 0 0 40px #00f5ff, 0 0 60px #00f5ff',
              animation: 'ledPulse 2s ease-in-out infinite alternate'
            }}
          >
            LED WALL SYSTEM
          </h3>

          {/* Light reflection effects */}
          <div className="absolute inset-0 pointer-events-none">
            <div 
              className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 opacity-0"
              style={{
                animation: 'lightSweep 6s linear infinite'
              }}
            />
          </div>
        </div>

        {/* Enhanced Descriptive Line */}
        <p className="text-xl md:text-2xl text-cyan-100 mb-12 max-w-4xl mx-auto leading-relaxed">
          <span className="inline-block animate-pulse">Advanced modular LED wall panels with 3D visual depth and synchronized sound</span>
          <br />
          <span className="text-blue-200">- designed by AlpBuild.</span>
        </p>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {[
            {
              icon: 'ri-3d-view-line',
              title: '3D Visual Depth',
              description: 'Immersive three-dimensional LED displays with stunning depth perception'
            },
            {
              icon: 'ri-sound-module-line',
              title: 'Synchronized Sound',
              description: 'Perfect audio-visual synchronization for complete sensory experiences'
            },
            {
              icon: 'ri-puzzle-2-line',
              title: 'Modular Design',
              description: 'Flexible modular panels that adapt to any space and configuration'
            }
          ].map((feature, index) => (
            <div 
              key={index}
              className="relative group"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="relative bg-gray-800/50 backdrop-blur-sm border border-cyan-500/30 rounded-xl p-6 hover:border-cyan-400/60 transition-all duration-300 group-hover:bg-gray-800/70">
                {/* Glowing border effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-cyan-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full shadow-lg shadow-cyan-500/50">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <h4 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {feature.title}
                  </h4>
                  
                  <p className="text-gray-300 group-hover:text-white transition-colors">
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="relative inline-block">
          <button className="relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-12 py-4 rounded-full text-lg font-bold hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-500/50 hover:shadow-cyan-400/60 whitespace-nowrap cursor-pointer">
            <span className="relative z-10">
              <i className="ri-eye-line mr-2"></i>
              Discover the System
            </span>
            
            {/* Button glow effect */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 hover:opacity-20 transition-opacity duration-300 blur-md" />
          </button>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes ledGlow {
          0%, 100% { 
            text-shadow: 0 0 10px #00f5ff, 0 0 20px #00f5ff, 0 0 30px #00f5ff;
            filter: brightness(1);
          }
          50% { 
            text-shadow: 0 0 20px #00f5ff, 0 0 40px #00f5ff, 0 0 60px #00f5ff;
            filter: brightness(1.2);
          }
        }

        @keyframes textShine {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes ledPulse {
          0% { 
            text-shadow: 0 0 20px #00f5ff, 0 0 40px #00f5ff, 0 0 60px #00f5ff;
          }
          100% { 
            text-shadow: 0 0 30px #00f5ff, 0 0 60px #00f5ff, 0 0 90px #00f5ff;
          }
        }

        @keyframes lightSweep {
          0% { 
            transform: translateX(-100%) skewX(-12deg);
            opacity: 0;
          }
          20% { 
            opacity: 1;
          }
          80% { 
            opacity: 1;
          }
          100% { 
            transform: translateX(100%) skewX(-12deg);
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default LedWallSystem;
